import React, { useState, useMemo } from "react";
import { Tag, Modal, Button, Space, message } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { uniqBy } from "lodash";
import usePayrollStore from "../../../config/stores/administration/usePayrollStore";
import AntdTable from "../../../components/common/AntdTable";
import TableTitle from "../../../components/common/TableTitle";
import TableButtonActions from "../../../components/common/TableButtonActions";
import appConfig from "../../../config/config.json";
import { searchTable } from "../../../config/constants";
import { FcDocument, FcFile } from "react-icons/fc";
import axios from "axios";

const { confirm } = Modal;

const PayrollListTable = ({ payrolls = [], month, year }) => {
  const payrollStore = usePayrollStore();
  const {
    deleteMonthlyPayroll,
    deletingPayroll,
    setShowPayrollDetails,
    setCreatingPayroll,
    getAllPayrolls,
    getPayrollDetails,
    setPayrollPeriod,
  } = payrollStore;
  const [deleteId, setDeleteId] = useState(null);
  const [filterTable, setFilterTable] = useState(null);

  const search = (value) => {
    const filtered = searchTable(payrolls, value);
    setFilterTable(filtered);
  };

  const setFilterTableNull = () => {
    setFilterTable(null);
  };

  const confirmDelete = async (id, month, year) => {
    setDeleteId(id);
    confirm({
      title: "Do you want to delete this payroll?",
      icon: <ExclamationCircleFilled />,
      content: "This action cannot be undone.",
      onOk: async () => {
        await deleteMonthlyPayroll(id, month, year);
        setDeleteId(null);
        getAllPayrolls();
      },
      onCancel() {
        setDeleteId(null);
      },
    });
  };

  const deleteRecord = (id) => {
    setDeleteId(id);
  };

  const viewDetails = async (id) => {
    await getPayrollDetails(id);
    setShowPayrollDetails(true, null);
    getAllPayrolls();
  };

  const updateRecord = (record) => {
    setCreatingPayroll(true, false, record);
  };

  const uniqueProjects = useMemo(() => {
    if (!payrolls || payrolls.length === 0) return [];
    
    const projects = [];
    
    const addProject = (id, name) => {
      if (id && name) {
        projects.push({ id: String(id), name });
      }
    };

    payrolls.forEach(record => {
      let allocations = record.projectAllocations;
      
      if (!allocations || !Array.isArray(allocations)) {
        allocations = record.project_allocations;
      }
      
      if (record.projectAllocations && !Array.isArray(record.projectAllocations) && typeof record.projectAllocations === 'object') {
        Object.keys(record.projectAllocations).forEach(key => {
          const alloc = record.projectAllocations[key];
          if (alloc && alloc.name) {
            addProject(key, alloc.name);
          }
        });
      }

      if (allocations && Array.isArray(allocations)) {
        allocations.forEach(alloc => {
          if (alloc.project) {
            addProject(alloc.project.id, alloc.project.name);
          } else if (alloc.project_id && alloc.project_name) {
             addProject(alloc.project_id, alloc.project_name);
          } else if (alloc.projectId && alloc.projectName) {
             addProject(alloc.projectId, alloc.projectName);
          } else if (alloc.item && alloc.key && alloc.key.startsWith('project_')) {
             addProject(alloc.key.replace('project_', ''), alloc.item);
          }
        });
      }
    });
    
    return uniqBy(projects, 'id').sort((a, b) => a.name.localeCompare(b.name));
  }, [payrolls]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = parseFloat(value);
    if (isNaN(num) || num === 0) return "-";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const hasAllowance = (field) => {
    return payrolls && payrolls.some(p => {
      const val = parseFloat(p[field]);
      return !isNaN(val) && val > 0;
    });
  };

  const columns = [
    {
      title: "Staff",
      fixed: "left",
      width: 200,
      render: (record) => (
        <span className="fw-bold">
          {`${record.user?.first_name || ""} ${record.user?.other_names || ""}`.trim() || record.name || "N/A"}
        </span>
      ),
    },
    ...uniqueProjects.map(proj => ({
      title: proj.name,
      key: `proj-${proj.id}`,
      width: 150,
      align: "right",
      render: (record) => {
        if (record.projectAllocations && !Array.isArray(record.projectAllocations) && typeof record.projectAllocations === 'object') {
          const alloc = record.projectAllocations[proj.id];
          if (alloc) return formatCurrency(alloc.amount);
        }

        let allocations = record.projectAllocations;
        if (!allocations || !Array.isArray(allocations)) {
          allocations = record.project_allocations;
        }

        const alloc = allocations?.find(a => 
          (a.project && String(a.project.id) === String(proj.id)) || 
          (a.project_id && String(a.project_id) === String(proj.id)) ||
          (a.projectId && String(a.projectId) === String(proj.id))
        );
        return formatCurrency(alloc?.amount);
      }
    })),
    {
      title: "Transport",
      dataIndex: "transport_allowance",
      width: 120,
      align: "right",
      render: formatCurrency,
    },
    {
      title: "Housing",
      dataIndex: "housing_allowance",
      width: 120,
      align: "right",
      render: formatCurrency,
    },
    {
      title: "Medical",
      dataIndex: "medical_allowances",
      width: 120,
      align: "right",
      render: formatCurrency,
    },
    {
      title: "Other",
      dataIndex: "other_allowances",
      width: 120,
      align: "right",
      render: formatCurrency,
    },
    {
      title: "Gross Pay",
      dataIndex: "gross_pay",
      align: "right",
      width: 150,
      className: "bg-light",
      render: (val) => <span className="fw-bold">{formatCurrency(val)}</span>,
    },
    {
      title: "PAYE",
      dataIndex: "paye",
      align: "right",
      width: 120,
      render: formatCurrency,
    },
    {
      title: "NSSF (5%)",
      dataIndex: "nssf_deduction",
      align: "right",
      width: 120,
      render: formatCurrency,
    },
    {
      title: "NSSF (10%)",
      align: "right",
      width: 120,
      render: (record) => {
        const grossPay = parseFloat(record.gross_pay) || 0;
        const nssfEmployer = grossPay * 0.1;
        return formatCurrency(nssfEmployer);
      },
    },
    {
      title: "Total Deductions",
      dataIndex: "total_deductions",
      align: "right",
      width: 150,
      className: "bg-light",
      render: (val) => <span className="fw-bold">{formatCurrency(val)}</span>,
    },
    {
      title: "Net Pay",
      dataIndex: "net_salary",
      align: "right",
      width: 150,
      render: (val) => <span className="fw-bold text-success">{formatCurrency(val)}</span>,
    },
    {
      title: "Status",
      align: "center",
      width: 100,
      render: (record) => {
        const status = record.status?.toLowerCase();
        if (status === "approved") return <Tag color="green">APPROVED</Tag>;
        if (status === "checked") return <Tag color="blue">CHECKED</Tag>;
        if (status === "rejected") return <Tag color="red">REJECTED</Tag>;
        return <Tag color="orange">PENDING</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (record) => {
        const status = record.status?.toLowerCase();
        const isApproved = status === "approved" || 
                          (appConfig.status?.approved && status === appConfig.status.approved.toLowerCase());

        return (
          <TableButtonActions
            record={record}
            confirmDelete={() => confirmDelete(record.id, record.month, record.year)}
            deleteLoading={deletingPayroll}
            viewDetails={() => viewDetails(record.id)}
            updateRecord={!isApproved ? () => updateRecord(record) : null}
            deleteId={deleteId}
            deleteRecord={!isApproved ? deleteRecord : null}
          />
        );
      },
    },
  ];

  const refreshTable = () => {
    getAllPayrolls();
  };

  const openAddModal = () => {
    if (month && year) {
      setPayrollPeriod({ month, year, details: "" });
      setCreatingPayroll(true, true, null);
    }
  };

  const getDownloadParams = () => {
    if (payrolls && payrolls.length > 0) {
      const firstRecord = payrolls[0];
      return { month: firstRecord.month, year: firstRecord.year };
    }
    return null;
  };

  const downloadReport = async (type) => {
    const params = getDownloadParams();
    if (!params) {
      message.warning("No payroll records available to export.");
      return;
    }

    try {
      const endpoint = type === 'pdf' ? '/payroll/download-pdf' : '/payroll/download-excel';
      const extension = type === 'pdf' ? 'pdf' : 'xlsx';
      
      const response = await axios.get(endpoint, {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Payroll_${params.month}_${params.year}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error(`Failed to download ${type.toUpperCase()} report.`);
    }
  };

  const exportActions = (
    <Space direction="horizontal">
      <Button 
        onClick={() => downloadReport('pdf')}
        disabled={!payrolls || payrolls.length === 0}
      >
        <Space>
          <FcDocument /> PDF
        </Space>
      </Button>
      <Button 
        onClick={() => downloadReport('excel')}
        disabled={!payrolls || payrolls.length === 0}
      >
        <Space>
          <FcFile /> Excel
        </Space>
      </Button>
    </Space>
  );

  return (
    <div>
      <AntdTable
        loading={payrollStore.gettingPayrolls}
        title={() => (
          <TableTitle
            setFilterTableNull={setFilterTableNull}
            openAddModal={openAddModal}
            refreshTable={refreshTable}
            search={search}
            exportRecords={null}
            rightActions={exportActions}
            addNewText="ADD NEW PAYROLL"
          />
        )}
        pageSize={20}
        data={filterTable === null ? payrolls || [] : filterTable}
        columns={columns}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default PayrollListTable;